import { Component, DestroyRef, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import {
  combineLatest,
  debounceTime,
  defer,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionStateService } from '../../../services/session-state.service';
import { WorkService } from '../../../services/work.service';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { TimerService } from '../../../services/timer.service';
import {
  FirestoreProject,
  FirestoreTimer,
  IsoDate,
  IsoDateTime,
  TimeEntry,
  timerTimeEntries,
  timeEntriesOverlap,
  WorkEntity2,
  workMinutesAfterTimeEntryUpdate,
  WorkPositionSummary,
} from '@jessie/commons';
import { TimerComponent, TimerData } from '../components/timer/timer.component';
import { ProjectService } from '../../../services/project.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { WorkForm } from '../../projects/slide-overs/work-slide-over.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { addMinutes, formatISO, isToday, isValid, parseISO } from 'date-fns';
import { isDeepEqual } from 'remeda';

interface FavoriteDisplayItem {
  _isFavorite: boolean;
  displayName: string;
}

function compareFavoriteDisplayItems<T extends FavoriteDisplayItem>(a: T, b: T): number {
  if (a._isFavorite && !b._isFavorite) return -1;
  if (!a._isFavorite && b._isFavorite) return 1;
  return a.displayName.localeCompare(b.displayName);
}

@Component({
  selector: 'app-timer-page',
  imports: [AsyncPipe, TimerComponent],
  templateUrl: './timer.page.html',
})
export class TimerPage {
  sessionState = inject(SessionStateService);
  workService = inject(WorkService);
  timerService = inject(TimerService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);

  private projectService = inject(ProjectService);
  private destroyRef = inject(DestroyRef);
  private saveStarted$ = new Subject<void>();
  private autosaveDisabled = false;
  private metadataAutosaveWrite: Promise<void> | null = null;
  private metadataAutosavePending = false;


  workForm!: FormGroup<WorkForm>;
  editingTimeEntryId: string | '__running' | null = null;
  timeEntryError: string | null = null;
  savingTimeEntryId: string | null = null;

  projects = this.projectService.getActiveProjects().pipe(shareReplay(1));

  projectSummaries = combineLatest([this.projects, this.sessionState.getUser()]).pipe(
    map(([projects, user]) =>
      projects
        .map((project) => ({
          id: project.id,
          displayName: project.data.name || 'No Displayname',
          _isFavorite: !!(user && project.data.favorite?.[user.id]),
        }))
        .sort(compareFavoriteDisplayItems)
        .map(({ id, displayName }) => ({ id, displayName }))
    )
  );

  positionSummaries: Observable<WorkPositionSummary[]> = defer(() =>
    combineLatest([
      this.workForm.controls.project.valueChanges.pipe(
        startWith(this.workForm.controls.project.value),
        switchMap((projectSummary) =>
          this.projects.pipe(
            map((projects) => projects.find((project) => project.id === projectSummary?.id) ?? null)
          )
        ),
        filter((project): project is FirestoreProject => !!project)
      ),
      this.sessionState.getUser(),
    ]).pipe(
      map(([project, user]) =>
        this.projectService
          .getWorkPositionSummaries(project)
          .map((pos) => ({
            ...pos,
            _isFavorite: !!(user && project.data.positions?.[pos.id]?.favorite?.[user.id]),
          }))
          .sort(compareFavoriteDisplayItems)
          .map(({ _isFavorite, ...rest }) => rest)
      )
    )
  );

  firestoreTimer = this.activatedRoute.params.pipe(
    map((params) => params.timerId),
    switchMap((timerId) => this.timerService.getTimer(timerId)),
    tap((firestoreTimer) => {
      if (this.workForm && !this.workForm.controls.workMinutes.enabled) {
        this.workForm.controls.workMinutes.patchValue(
          firestoreTimer.data.workItemState === 'embedded'
            ? (firestoreTimer.data.work.workMinutes ?? 0)
            : 0,
          { emitEvent: false }
        );
      }
      this.setWorkMinutesEnabled(firestoreTimer.data.timerState);
      this.reconcileTimeEntryEditor(firestoreTimer);
    })
  );

  timer: Observable<TimerData> = this.firestoreTimer.pipe(
    map((firestoreTimer) => ({
      isToday: isToday(parseISO(firestoreTimer.data.date)),
      state: firestoreTimer.data.timerState,
      workItemState: firestoreTimer.data.workItemState,
      date: firestoreTimer.data.date,
      timeEntries:
        firestoreTimer.data.workItemState === 'embedded'
          ? (this.mapTimeEntries(firestoreTimer.data.work.timeEntries) ?? null)
          : null,
      startTime: firestoreTimer.data.timerState === 'active' ? firestoreTimer.data.startTime : null,
    }))
  );

  private mapTimeEntries(
    timeEntries: Record<string, TimeEntry> | null | undefined
  ): NonNullable<TimerData['timeEntries']> | undefined {
    if (!timeEntries) {
      return;
    }

    return Object.entries(timeEntries).map(([id, entry]) => {
      const start = parseISO(entry.startTime);
      const end = addMinutes(start, entry.totalMinutes);
      const isSameDay =
        isValid(start) &&
        isValid(end) &&
        formatISO(start, { representation: 'date' }) === formatISO(end, { representation: 'date' });

      return {
        id,
        startTime: entry.startTime,
        endTime: isValid(end) ? end.toISOString() : entry.startTime,
        totalMinutes: entry.totalMinutes,
        isSameDay,
      };
    });
  }

  async ngOnInit() {
    const timer = await firstValueFrom(this.firestoreTimer);
    if (timer.data.workItemState === 'embedded') {
      await this.initForm(timer.data.work);
    }
  }

  async navigateBack() {
    await this.router.navigate(['../..'], { relativeTo: this.activatedRoute });
  }

  async start() {
    const timer = await firstValueFrom(this.firestoreTimer);
    await this.timerService.startTimer(timer.id);
  }

  async stop() {
    await this.timerService.stopTimer();
  }

  async delete() {
    const timer = await firstValueFrom(this.firestoreTimer);
    await this.timerService.deleteTimer(timer);
    await this.router.navigate(['../..'], { relativeTo: this.activatedRoute });
  }

  beginTimeEntryEdit(timeEntryId: string | '__running') {
    if (this.editingTimeEntryId || this.savingTimeEntryId) {
      return;
    }

    this.editingTimeEntryId = timeEntryId;
  }

  cancelTimeEntryEdit() {
    if (this.savingTimeEntryId) {
      return;
    }

    this.editingTimeEntryId = null;
    this.timeEntryError = null;
  }

  async updateTimeEntry({
    sessionEntryId,
    nextEntry,
  }: {
    sessionEntryId: string;
    nextEntry: TimeEntry;
  }) {
    if (this.editingTimeEntryId !== sessionEntryId || this.savingTimeEntryId) {
      return;
    }

    this.savingTimeEntryId = sessionEntryId;
    if (this.metadataAutosaveWrite) {
      await this.metadataAutosaveWrite.catch(() => undefined);
    }
    try {
      const timer = await firstValueFrom(this.firestoreTimer);
      if (
        timer.data.timerState !== 'inactive' ||
        timer.data.workItemState !== 'embedded'
      ) {
        this.setTimeEntryError(sessionEntryId, 'Das Ende muss nach dem Start liegen.');
        return;
      }

      const currentEntry = timer.data.work.timeEntries?.[sessionEntryId];
      if (!currentEntry) {
        this.setTimeEntryError(sessionEntryId, 'Das Ende muss nach dem Start liegen.');
        return;
      }

      const currentStart = parseISO(currentEntry.startTime);
      const currentEnd = addMinutes(currentStart, currentEntry.totalMinutes);
      const nextStart = parseISO(nextEntry.startTime);
      const nextEnd = addMinutes(nextStart, nextEntry.totalMinutes);
      if (
        !isValid(currentStart) ||
        !isValid(currentEnd) ||
        !isValid(nextStart) ||
        !isValid(nextEnd) ||
        !Number.isFinite(nextEntry.totalMinutes) ||
        nextEntry.totalMinutes < 1
      ) {
        this.setTimeEntryError(sessionEntryId, 'Das Ende muss nach dem Start liegen.');
        return;
      }

      const entryDate = formatISO(currentStart, { representation: 'date' });
      if (
        formatISO(currentEnd, { representation: 'date' }) !== entryDate ||
        formatISO(nextStart, { representation: 'date' }) !== entryDate ||
        formatISO(nextEnd, { representation: 'date' }) !== entryDate
      ) {
        this.setTimeEntryError(sessionEntryId, 'Das Ende muss nach dem Start liegen.');
        return;
      }

      const orgaUserId = timer.data.work.orgaUser?.id;
      if (!orgaUserId) {
        throw new Error('Embedded timer has no organisation user');
      }

      const now = new Date();
      if (
        await this.hasTimeEntryCollision(
          nextEntry,
          entryDate,
          orgaUserId,
          timer.id,
          sessionEntryId,
          now
        )
      ) {
        this.setTimeEntryError(
          sessionEntryId,
          'Der gewählte Zeitraum überschneidet sich mit einem bestehenden Eintrag.'
        );
        return;
      }

      await this.timerService.updateEmbeddedTimeEntry(timer, sessionEntryId, nextEntry);
      if (this.workForm) {
        this.workForm.controls.workMinutes.patchValue(
          workMinutesAfterTimeEntryUpdate(
            timer.data.work.workMinutes ?? 0,
            currentEntry,
            nextEntry.totalMinutes
          ),
          { emitEvent: false }
        );
      }
      if (this.editingTimeEntryId === sessionEntryId) {
        this.editingTimeEntryId = null;
        this.timeEntryError = null;
      }
    } catch {
      this.setTimeEntryError(sessionEntryId, 'Die Zeitänderung konnte nicht gespeichert werden.');
    } finally {
      if (this.savingTimeEntryId === sessionEntryId) {
        this.savingTimeEntryId = null;
      }
      this.flushDeferredMetadataAutosave();
    }
  }

  async updateActiveStartTime(startTime: IsoDateTime) {
    if (this.editingTimeEntryId !== '__running' || this.savingTimeEntryId) {
      return;
    }

    this.savingTimeEntryId = '__running';
    if (this.metadataAutosaveWrite) {
      await this.metadataAutosaveWrite.catch(() => undefined);
    }
    try {
      const now = new Date();
      const timer = await firstValueFrom(this.firestoreTimer);
      if (
        timer.data.timerState !== 'active' ||
        timer.data.workItemState !== 'embedded' ||
        timer.data.date !== formatISO(now, { representation: 'date' })
      ) {
        this.setTimeEntryError('__running', 'Das Ende muss nach dem Start liegen.');
        return;
      }

      const nextStart = parseISO(startTime);
      if (
        !isValid(nextStart) ||
        formatISO(nextStart, { representation: 'date' }) !== timer.data.date ||
        nextStart.getTime() >= now.getTime()
      ) {
        this.setTimeEntryError('__running', 'Das Ende muss nach dem Start liegen.');
        return;
      }

      const orgaUserId = timer.data.work.orgaUser?.id;
      if (!orgaUserId) {
        throw new Error('Embedded timer has no organisation user');
      }

      const nextEntry: TimeEntry = {
        startTime,
        totalMinutes: Math.max(1, Math.ceil((now.getTime() - nextStart.getTime()) / 60_000)),
      };
      if (
        await this.hasTimeEntryCollision(
          nextEntry,
          timer.data.date,
          orgaUserId,
          timer.id,
          '__running',
          now
        )
      ) {
        this.setTimeEntryError(
          '__running',
          'Der gewählte Zeitraum überschneidet sich mit einem bestehenden Eintrag.'
        );
        return;
      }

      await this.timerService.updateActiveStartTime(timer, startTime);
      if (this.editingTimeEntryId === '__running') {
        this.editingTimeEntryId = null;
        this.timeEntryError = null;
      }
    } catch {
      this.setTimeEntryError('__running', 'Die Zeitänderung konnte nicht gespeichert werden.');
    } finally {
      if (this.savingTimeEntryId === '__running') {
        this.savingTimeEntryId = null;
      }
      this.flushDeferredMetadataAutosave();
    }
  }

  async saveWork() {
    if (
      !this.workForm ||
      this.editingTimeEntryId !== null ||
      this.savingTimeEntryId !== null
    ) {
      return;
    }

    this.workForm.markAllAsTouched();
    if (!this.workForm.valid) {
      return;
    }

    const timer = await firstValueFrom(this.firestoreTimer);
    if (timer.data.workItemState !== 'embedded') {
      return;
    }

    this.autosaveDisabled = true;
    try {
      const formValue = this.workForm.getRawValue();
      const timeEntries = this.getNonEmptyTimeEntries(timer.data.work.timeEntries);
      await this.workService.createWorkFromTimer(timer, {
        ...formValue,
        workMinutes: formValue.workMinutes,
        invoice: null,
        timeEntries,
      } as WorkEntity2);
      this.saveStarted$.next();
      await this.router.navigate(['../..'], { relativeTo: this.activatedRoute });
    } catch {
      this.autosaveDisabled = false;
      await this.updateWork();
    }
  }

  private setTimeEntryError(
    editorId: string | '__running',
    error: string
  ) {
    if (this.editingTimeEntryId === editorId) {
      this.timeEntryError = error;
    }
  }

  private reconcileTimeEntryEditor(timer: FirestoreTimer) {
    if (!this.editingTimeEntryId) {
      return;
    }

    let isEditable = false;
    if (this.editingTimeEntryId === '__running') {
      isEditable =
        timer.data.timerState === 'active' &&
        timer.data.workItemState === 'embedded' &&
        isToday(parseISO(timer.data.date));
    } else if (
      timer.data.timerState === 'inactive' &&
      timer.data.workItemState === 'embedded'
    ) {
      const entry = timer.data.work.timeEntries?.[this.editingTimeEntryId];
      const start = entry ? parseISO(entry.startTime) : null;
      const end = start && entry ? addMinutes(start, entry.totalMinutes) : null;
      isEditable =
        !!start &&
        !!end &&
        isValid(start) &&
        isValid(end) &&
        formatISO(start, { representation: 'date' }) ===
        formatISO(end, { representation: 'date' });
    }

    if (!isEditable) {
      this.editingTimeEntryId = null;
      this.timeEntryError = null;
    }
  }

  private flushDeferredMetadataAutosave() {
    if (!this.metadataAutosavePending || this.autosaveDisabled) {
      return;
    }

    this.metadataAutosavePending = false;
    void this.updateWork();
  }

  private async hasTimeEntryCollision(
    nextEntry: TimeEntry,
    date: IsoDate,
    orgaUserId: string,
    excludedTimerId: string,
    excludedTimeEntryId: string,
    now: Date
  ): Promise<boolean> {
    const [works, timers] = await Promise.all([
      firstValueFrom(this.workService.getAllWorkForUser(orgaUserId, date, date)),
      firstValueFrom(this.timerService.getTimersByDate(date)),
    ]);

    for (const work of works) {
      for (const timeEntry of Object.values(work.data.timeEntries ?? {})) {
        if (timeEntriesOverlap(nextEntry, timeEntry)) {
          return true;
        }
      }
    }

    for (const timer of timers) {
      for (const [timeEntryId, timeEntry] of Object.entries(timerTimeEntries(timer.data, now))) {
        if (timer.id === excludedTimerId && timeEntryId === excludedTimeEntryId) {
          continue;
        }

        if (timeEntriesOverlap(nextEntry, timeEntry)) {
          return true;
        }
      }
    }

    return false;
  }

  private getNonEmptyTimeEntries(
    timeEntries: Record<string, TimeEntry> | null | undefined
  ): Record<string, TimeEntry> | null {
    return timeEntries && Object.keys(timeEntries).length > 0 ? timeEntries : null;
  }

  private async initForm(work: Partial<WorkEntity2>) {
    this.workForm = new FormGroup<WorkForm>({
      orgaUser: new FormControl(work.orgaUser!, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      project: new FormControl(work?.project ?? null, [Validators.required]),
      position: new FormControl(work?.position ?? null, [Validators.required]),
      date: new FormControl(work.date!, [Validators.required]),
      workMinutes: new FormControl(work?.workMinutes ?? null, [Validators.required]),
      description: new FormControl(work?.description ?? null, [Validators.maxLength(240)]),
      customer: new FormControl(work?.customer ?? null),
    });

    this.workForm.controls.project.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (project) => {
        this.workForm.controls.customer.patchValue(
          project ? await this.getCustomer(project.id) : null
        );
        this.workForm.controls.position.patchValue(null);
      });

    this.workForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(isDeepEqual),
        takeUntil(this.saveStarted$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.updateWork());

    const timer = await firstValueFrom(this.firestoreTimer);
    this.setWorkMinutesEnabled(timer.data.timerState);
  }

  private setWorkMinutesEnabled(timerState: 'active' | 'inactive') {
    if (!this.workForm) {
      return;
    }

    if (timerState === 'active') {
      this.workForm.controls.workMinutes.disable({ emitEvent: false });
    } else {
      this.workForm.controls.workMinutes.enable({ emitEvent: false });
    }
  }

  private async getCustomer(projectId: string) {
    const project = await firstValueFrom(
      this.projects.pipe(map((projects) => projects.find((project) => projectId === project.id)))
    );
    return project?.data.customerId
      ? { id: project.data.customerId, displayName: project.data.customerName as string }
      : null;
  }

  private async updateWork() {
    if (this.autosaveDisabled) {
      return;
    }
    if (this.savingTimeEntryId) {
      this.metadataAutosavePending = true;
      return;
    }

    const timer = await firstValueFrom(this.firestoreTimer);
    if (this.autosaveDisabled) {
      return;
    }
    if (this.savingTimeEntryId) {
      this.metadataAutosavePending = true;
      return;
    }

    const write = this.timerService.updateEmbeddedWork(
      timer,
      this.workForm.value as WorkEntity2
    );
    this.metadataAutosaveWrite = write;
    try {
      await write;
    } finally {
      if (this.metadataAutosaveWrite === write) {
        this.metadataAutosaveWrite = null;
      }
    }
  }
}
